import { MdCheck } from 'react-icons/md';
import { ReportingControllerBaseConfig } from '../../../../../base';
import { DatetimeElementController } from './datetimeElementInterface';
import { DatetimeElementViewProps } from './datetimeElementProps';

function formatDisplayValue(isoString: string | null): string {
    if (!isoString) return 'Empty';

    try {
        const date = new Date(isoString);
        // Format as readable datetime: "Dec 25, 2023 at 2:30 PM"
        return date.toLocaleString('en-US', {
            month: 'short',
            day: 'numeric',
            year: 'numeric',
            hour: 'numeric',
            minute: '2-digit',
            hour12: true,
        });
    } catch {
        return 'Invalid date';
    }
}

function formatInputValue(isoString: string | null): string {
    if (!isoString) return '';

    try {
        const date = new Date(isoString);
        // Format for datetime-local input using local time: "2023-12-25T14:30"
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        const hours = String(date.getHours()).padStart(2, '0');
        const minutes = String(date.getMinutes()).padStart(2, '0');

        return `${year}-${month}-${day}T${hours}:${minutes}`;
    } catch {
        return '';
    }
}

function parseInputValue(inputValue: string): string | null {
    if (!inputValue.trim()) return null;

    try {
        // Convert datetime-local format to ISO string
        const date = new Date(inputValue);
        return date.toISOString();
    } catch {
        return null;
    }
}

export function createDatetimeElementController(
    config: ReportingControllerBaseConfig
): DatetimeElementController {
    return {
        useProps(props): DatetimeElementViewProps {
            return {
                containerRef: props.containerRef,
                isEmpty: props.field.value === null,
                edit: {
                    value: props.isEditing,
                    button: {
                        onClick() {
                            props.onEdit();
                        },
                    },
                },
                submit: {
                    label: 'Save',
                    Icon: MdCheck,
                    button: {
                        isDisabled: !props.field.isDirty,
                        onClick(event) {
                            props.onSubmit(props.field.value);
                            event.preventDefault();
                            event.stopPropagation();
                        },
                        // NOTE Need to prevent mouse down event from triggering blur handler
                        onMouseDown(event) {
                            event.preventDefault();
                        },
                    },
                },
                label: formatDisplayValue(props.field.value),
                input: {
                    onKeyDown(event) {
                        if (event.key === 'Enter' && props.isEditing) {
                            props.onSubmit(props.field.value);
                            event.preventDefault();
                            event.stopPropagation();
                        }
                        if (event.key === 'Escape' && props.isEditing) {
                            props.onCancel();
                            event.preventDefault();
                            event.stopPropagation();
                        }
                    },
                    value: formatInputValue(props.field.value),
                    onChange(event) {
                        const parsedValue = parseInputValue(event.currentTarget.value);
                        props.field.onChange(parsedValue);
                    },
                    onBlur(event) {
                        props.onBlur();
                    },
                },
            };
        },
    };
}
